import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Row, Col, Card, Button, Spin, Alert, Tag, theme } from 'antd';
import { Link } from 'react-router-dom';
import { ArrowRightOutlined, DatabaseOutlined, TeamOutlined, SafetyOutlined, CalendarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { fetchPortalHome } from '../../services/portalApi';
import PortalHtml from '../../components/portal/PortalHtml';
import CountUpStat from '../../components/portal/CountUpStat';
import ShinyText from '../../components/blocks/TextAnimations/ShinyText/ShinyText';
import mappingFigure from '../../assets/images/fphys-12-646023-g005.webp';
import '../../components/portal/portal.css';

const { Title, Paragraph, Text } = Typography;

const LatestPostsCarousel = ({ posts }) => {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const syncArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(max > 8 && el.scrollLeft < max - 8);
  }, []);

  useEffect(() => {
    syncArrows();
    window.addEventListener('resize', syncArrows);
    return () => window.removeEventListener('resize', syncArrows);
  }, [posts, syncArrows]);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('.portal-post-carousel-card');
    const w = card ? card.getBoundingClientRect().width + 20 : 340;
    el.scrollBy({ left: dir * w, behavior: 'smooth' });
  };

  return (
    <section className="portal-post-carousel-section" aria-labelledby="latest-updates-heading">
      <div className="portal-post-carousel-head">
        <Title id="latest-updates-heading" level={3} style={{ margin: 0 }}>
          Latest updates
        </Title>
        <div className="portal-post-carousel-head-actions">
          {(canPrev || canNext) && (
            <div className="portal-post-carousel-nav">
              <Button
                type="text"
                shape="circle"
                aria-label="Previous posts"
                disabled={!canPrev}
                icon={<LeftOutlined />}
                onClick={() => scrollByCard(-1)}
              />
              <Button
                type="text"
                shape="circle"
                aria-label="Next posts"
                disabled={!canNext}
                icon={<RightOutlined />}
                onClick={() => scrollByCard(1)}
              />
            </div>
          )}
          <Link to="/posts" className="portal-post-carousel-more">
            More <ArrowRightOutlined />
          </Link>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="portal-post-carousel"
        onScroll={syncArrows}
      >
        {posts.map((post) => (
          <div key={post.id} className="portal-post-carousel-card">
            <Link to={`/posts/${post.slug}`} className="portal-post-card">
              {post.featuredMediaUrl ? (
                <img
                  className="portal-post-carousel-media"
                  src={post.featuredMediaUrl}
                  alt=""
                />
              ) : (
                <div className="portal-post-carousel-media portal-post-carousel-media--empty" />
              )}
              <div className="portal-post-carousel-body">
                {post.categoryName && (
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, letterSpacing: 0.4, marginBottom: 6 }}>
                    {post.categoryName}
                  </Text>
                )}
                {post.needsPassword && <Tag style={{ marginBottom: 8 }}>Password</Tag>}
                <Title level={4} style={{ margin: '0 0 8px' }}>{post.title}</Title>
                {post.excerpt && (
                  <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 8 }}>
                    {post.excerpt}
                  </Paragraph>
                )}
                {post.publishedAt && (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {new Date(post.publishedAt).toLocaleDateString()}
                  </Text>
                )}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

const FEATURES = [
  {
    icon: <DatabaseOutlined style={{ fontSize: 30, color: '#005EB8' }} />,
    title: 'Open, structured EP data',
    description: 'Electroanatomic mapping and ablation data captured in a consistent, research-ready format across sites.',
  },
  {
    icon: <TeamOutlined style={{ fontSize: 30, color: '#005EB8' }} />,
    title: 'UK-wide collaboration',
    description: 'A shared platform for participating electrophysiology centres to contribute and compare studies.',
  },
  {
    icon: <SafetyOutlined style={{ fontSize: 30, color: '#005EB8' }} />,
    title: 'Governed & consented',
    description: 'Built for NHS-aligned governance, with role-based access and aggregate-only public reporting.',
  },
];

const PortalHomeScreen = () => {
  const { token: { colorPrimary } } = theme.useToken();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchPortalHome()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const widgets = useMemo(() => {
    try {
      return JSON.parse(data?.site?.homeWidgetsJson || '[]');
    } catch {
      return [];
    }
  }, [data]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} style={{ marginTop: 24 }} />;
  }

  const site = data?.site || {};
  const hasMetrics = widgets.some((w) => w.type === 'metric');
  const metrics = data.metrics || [];
  const richtextWidget = widgets.find((w) => w.type === 'richtext');
  const latestPosts = data.latestPosts || [];

  return (
    <div>
      {/* Full-bleed hero — breaks out of the centered content column, like a trust landing page. */}
      <div
        style={{
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          width: '100vw',
          background: colorPrimary,
          color: '#fff',
          padding: '96px 24px 88px',
        }}
      >
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, letterSpacing: 3, fontSize: 12, textTransform: 'uppercase' }}>
            Open Research Platform
          </Text>
          <Title
            style={{ color: '#fff', margin: '14px 0 20px', fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 600, letterSpacing: 0.2 }}
          >
            {site.heroTitle || 'OpenEP|NET'}
          </Title>
          {site.heroSubtitle && (
            <Paragraph style={{ color: 'rgba(255,255,255,0.88)', fontSize: 19, maxWidth: 640, marginBottom: 36, fontWeight: 300 }}>
              <ShinyText text={site.heroSubtitle} speed={6} />
            </Paragraph>
          )}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to={site.heroCtaHref || '/posts'}>
              <Button type="primary" size="large" icon={<ArrowRightOutlined />} style={{ background: '#fff', color: colorPrimary, border: 'none', fontWeight: 600 }}>
                {site.heroCtaLabel || 'Read updates'}
              </Button>
            </Link>
            <Link to="/userlogin">
              <Button size="large" style={{ background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Staff login
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Allowlisted live totals */}
      {hasMetrics && metrics.length > 0 && (
        <div style={{ padding: '48px 0 8px' }}>
          <Row gutter={[24, 24]} justify="center">
            {metrics.map((m) => (
              <Col xs={24} sm={8} key={m.key}>
                <CountUpStat value={m.value} label={m.label} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* About / feature grid */}
      <div style={{ padding: '64px 0' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 48, fontWeight: 600 }}>
          About OpenEP|NET
        </Title>
        <Row gutter={[24, 24]}>
          {FEATURES.map((f) => (
            <Col xs={24} sm={8} key={f.title}>
              <Card style={{ height: '100%', borderRadius: 10, border: '1px solid #eceff2' }} bodyStyle={{ padding: 32, textAlign: 'center' }}>
                <div style={{ marginBottom: 12 }}>{f.icon}</div>
                <Title level={4} style={{ marginBottom: 8 }}>{f.title}</Title>
                <Text type="secondary">{f.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {richtextWidget?.html && (
        <PortalHtml html={richtextWidget.html} style={{ marginBottom: 48 }} />
      )}

      {/* Science illustration */}
      <div style={{ padding: '8px 0 56px', textAlign: 'center' }}>
        <img
          src={mappingFigure}
          alt="Comparison of Carto and OpenEP local activation time maps"
          style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
          Local activation time maps: proprietary Carto export vs. OpenEP-generated maps.
        </Text>
      </div>

      {latestPosts.length > 0 && <LatestPostsCarousel posts={latestPosts} />}
    </div>
  );
};

export default PortalHomeScreen;
